import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import * as Sentry from '@sentry/gatsby';
import React from 'react';
import type { IIdleTimerProps } from 'react-idle-timer';
import { useIdleTimer } from 'react-idle-timer';
import { defineMessages, useIntl } from 'react-intl';

// 15 Minutes
const IDLE_TIMEOUT = 900_000;
// Show prompt at 1 minute remaining
const PROMPT_TIMEOUT = 60_000;

interface IdleTimerProps {
  onIdle: () => void;
  disabled: boolean;
}

const messages = defineMessages({
  promptTitle: {
    id: 'idleTimer.prompt.title',
    defaultMessage: 'Are you still there?',
  },
  promptBody: {
    id: 'idleTimer.prompt.body',
    defaultMessage: 'Your session will expire in {remaining} seconds',
  },
  promptButton: {
    id: 'idleTimer.prompt.button',
    defaultMessage: 'Still here',
  },
});

function IdleTimer(props: IdleTimerProps) {
  const [open, setOpen] = React.useState(false);
  const [remaining, setRemaining] = React.useState<number>(IDLE_TIMEOUT);
  const requestRef = React.useRef<number | null>(null);
  const { formatMessage } = useIntl();

  const onIdle: IIdleTimerProps['onIdle'] = (_event, idleTimer) => {
    // Capture everything about the timer
    const extras = {
      idleTime: idleTimer?.getIdleTime(),
      elapsedTime: idleTimer?.getElapsedTime(),
      activeTime: idleTimer?.getActiveTime(),
      remainingTime: idleTimer?.getRemainingTime(),
      totalIdleTime: idleTimer?.getTotalIdleTime(),
      totalElapsedTime: idleTimer?.getTotalElapsedTime(),
      totalActiveTime: idleTimer?.getTotalActiveTime(),
      lastActiveTime: idleTimer?.getLastActiveTime(),
      lastIdleTime: idleTimer?.getLastIdleTime(),
      isIdle: idleTimer?.isIdle(),
      isLastActiveTab: idleTimer?.isLastActiveTab(),
      isPrompted: idleTimer?.isPrompted(),
      isLeader: idleTimer?.isLeader(),
      tabId: idleTimer?.getTabId(),
    };
    Sentry.withScope((scope) => {
      scope.setExtras(extras);
      // Capture exception to view a replay
      scope.captureException('Session has expired');
    });
    props.onIdle();
    setOpen(false);
  };

  const onActive = () => {
    setOpen(false);
  };

  const onPrompt = () => {
    setOpen(true);
  };

  const { activate, getRemainingTime } = useIdleTimer({
    onIdle,
    onActive,
    onPrompt,
    timeout: IDLE_TIMEOUT,
    promptBeforeIdle: PROMPT_TIMEOUT,
    throttle: 500,
    disabled: props.disabled,
  });

  const updateRemainingTime = React.useCallback(() => {
    setRemaining(Math.ceil(getRemainingTime() / 1000));
    requestRef.current = requestAnimationFrame(updateRemainingTime);
  }, [getRemainingTime]);

  React.useEffect(() => {
    requestRef.current = requestAnimationFrame(updateRemainingTime);

    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [updateRemainingTime]);

  const handleStillHere = () => {
    setOpen(false);
    activate();
  };

  const _remaining = open ? remaining : '';

  return (
    <Dialog maxWidth="xl" open={open}>
      <DialogTitle>{formatMessage(messages.promptTitle)}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {formatMessage(messages.promptBody, { remaining: _remaining })}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleStillHere}>
          {formatMessage(messages.promptButton)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default IdleTimer;
